import { Box, Typography } from '@material-ui/core'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import IconContentAdd from '@material-ui/icons/Add'
import IconCancel from '@material-ui/icons/Cancel'
import Alert from '@material-ui/lab/Alert'
import AlertTitle from '@material-ui/lab/AlertTitle'
import { useState } from 'react'
import {
  Button as RaButton,
  DateInput,
  FormWithRedirect,
  required,
  SaveButton,
  TextInput,
  useNotify,
  useRefresh,
} from 'react-admin'

import { captureExceptionIfProduction } from 'helpers/utils'
import { deleteInsurancePeriod, updateInsurancePeriod } from '../../api/core'
import { withDisableBackdropClick } from '../../helpers/withDisableBackdropClick'

export function UpdateInsurancePeriodButton({
  equipment,
  equipmentType,
  existingInsurancePeriod = null,
}: any) {
  const [showDialog, setShowDialog] = useState(false)
  const [loading, setLoading] = useState(false)
  const [finalConfirmation, setFinalConfirmation] = useState(false)
  const [deleteConfirmation, setDeleteConfirmation] = useState(false)
  const notify = useNotify()
  const refresh = useRefresh()

  const handleClick = () => setShowDialog(true)
  const handleClose = () => setShowDialog(false)

  const handleFinalConfirmationOpen = () => setFinalConfirmation(true)
  const handleFinalConfirmationClose = () => setFinalConfirmation(false)

  const handleDeleteConfirmationOpen = () => setDeleteConfirmation(true)
  const handleDeleteConfirmationClose = () => setDeleteConfirmation(false)

  const initialValues = existingInsurancePeriod
    ? {
        date_start: existingInsurancePeriod.date_range_start,
        date_end: existingInsurancePeriod.date_range_end,
        date_range_end_reason: existingInsurancePeriod.date_range_end_reason,
        insurance_period_id: existingInsurancePeriod.id,
      }
    : null

  const handleSubmit = async (values: any) => {
    setLoading(true)
    if (!values.date_end) {
      values.date_end = null
    }
    updateInsurancePeriod(equipment.id, values, equipmentType)
      .then(() => {
        refresh()
        notify('Success!', { type: 'success' })
        setShowDialog(false)
      })
      .catch((error) => {
        const error_mesasge = error.response.data
          ? JSON.stringify(error.response.data)
          : 'Something went wrong'
        notify(error_mesasge, { type: 'error' })
        captureExceptionIfProduction(error)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const handleDelete = async () => {
    setLoading(true)

    deleteInsurancePeriod(equipment.id, initialValues, equipmentType)
      .then(() => {
        refresh()
        notify('Success!', { type: 'success' })
        setShowDialog(false)
      })
      .catch((error) => {
        notify(error.response.data, { type: 'error' })
        captureExceptionIfProduction(error)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const validatePeriods = (values: any) => {
    var errors: any = {}
    if (values.date_end && values.date_end <= values.date_start) {
      errors.date_end = ['This value must be later than date_start']
    }
    return errors
  }

  const buttonLabel = existingInsurancePeriod ? 'Edit' : 'Add'

  return (
    <>
      <RaButton onClick={handleClick} label={buttonLabel}>
        <IconContentAdd />
      </RaButton>
      <Dialog
        fullWidth
        open={showDialog}
        onClose={withDisableBackdropClick(handleClose)}
        aria-label={buttonLabel}
      >
        <DialogTitle>{buttonLabel}</DialogTitle>
        <FormWithRedirect
          initialValues={initialValues}
          resource="trucks"
          save={handleSubmit}
          validate={validatePeriods}
          render={({ handleSubmitWithRedirect, saving }) => (
            <>
              <DialogContent>
                <Box display="flex" flexDirection="column">
                  <Typography variant="body2">
                    Start Date: The first day the truck is under our insurance
                    <br />
                    End Date: The last day the truck is under our insurance.
                    <br />
                    NOTE: We will send an email to the insurance company the day after the end date.
                  </Typography>
                  <Box display="flex" flexDirection="column">
                    <Box display="flex" flexDirection="row">
                      <Box flex={1}>
                        <DateInput source="date_start" validate={required()} />
                      </Box>
                      <Box flex={1}>
                        <DateInput source="date_end" label="Date end" />
                      </Box>
                    </Box>
                    <TextInput source="date_range_end_reason" label="Reason for ending insurance" />
                  </Box>
                </Box>
              </DialogContent>
              <DialogActions>
                <Box display="flex">
                  {finalConfirmation && (
                    <Box flex={1}>
                      <Button onClick={handleFinalConfirmationClose} disabled={loading}>
                        <IconCancel />
                      </Button>
                    </Box>
                  )}
                  {deleteConfirmation && (
                    <Box flex={1}>
                      <Button onClick={handleDeleteConfirmationClose} disabled={loading}>
                        <IconCancel />
                      </Button>
                    </Box>
                  )}
                </Box>
                {!finalConfirmation && !deleteConfirmation && (
                  <Box flex={1}>
                    <Button onClick={handleClose}>Cancel</Button>
                  </Box>
                )}
                {!finalConfirmation && !deleteConfirmation && (
                  <Box flex={1}>
                    <Button variant="contained" color="primary" onClick={handleFinalConfirmationOpen}>
                      Looks good
                    </Button>
                  </Box>
                )}
                {finalConfirmation && (
                  <Box flex={1}>
                    <SaveButton
                      handleSubmitWithRedirect={handleSubmitWithRedirect}
                      saving={saving}
                      disabled={loading}
                    />
                  </Box>
                )}
                {!finalConfirmation && !deleteConfirmation && existingInsurancePeriod && (
                  <Box flex={1}>
                    <Button variant="outlined" color="primary" onClick={handleDeleteConfirmationOpen}>
                      Delete
                    </Button>
                  </Box>
                )}
                {deleteConfirmation && (
                  <Box flex={1}>
                    <Button variant="outlined" color="primary" onClick={handleDelete}>
                      Delete
                    </Button>
                  </Box>
                )}
              </DialogActions>
              <DialogContent>
                {finalConfirmation && (
                  <Alert severity="warning">
                    <AlertTitle>Warning</AlertTitle>
                    Are you sure? If you are setting a new insurance period, an automated email will be sent
                    to the insurance company on the start date. If you are closing an existing insurance
                    period, an automated email will be sent the first day after the final day in the period.
                  </Alert>
                )}
                {deleteConfirmation && (
                  <Alert severity="warning">
                    <AlertTitle>Warning</AlertTitle>
                    Are you sure? Insurance company will be notified that the insurance coverage for this
                    period is no longer active.
                  </Alert>
                )}
              </DialogContent>
            </>
          )}
        />
      </Dialog>
    </>
  )
}
